import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import { fetchCurrentStock } from "../../core/redux/slices/stockSlice";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const CurrentStock = () => {
  const dispatch = useDispatch();
  const { currentStock, loading } = useSelector((s) => s.stock);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  useEffect(() => { dispatch(fetchCurrentStock()); }, [dispatch]);

  const categories = useMemo(() => {
    const cats = [...new Set(currentStock.map((p) => p.itemCategory).filter(Boolean))];
    return ["All", ...cats];
  }, [currentStock]);

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return currentStock.filter(
      (p) =>
        (categoryFilter === "All" || p.itemCategory === categoryFilter) &&
        (!q || p.itemName?.toLowerCase().includes(q))
    );
  }, [currentStock, search, categoryFilter]);

  const lowStockCount = data.filter((p) => (p.currentStock || 0) <= (p.minimumStockLevel || 0)).length;

  const columns = [
    { title: "Product Name", dataIndex: "itemName", sorter: (a, b) => (a.itemName || "").localeCompare(b.itemName || "") },
    { title: "Category", dataIndex: "itemCategory", width: 120 },
    { title: "Unit", dataIndex: "unit", width: 80 },
    {
      title: "Current Stock", dataIndex: "currentStock", align: "center", width: 130,
      render: (v, rec) => {
        const stock = Number(v || 0);
        const min = Number(rec.minimumStockLevel || 0);
        return (
          <span className={stock <= min ? "text-danger fw-bold" : stock <= min * 1.5 ? "text-warning fw-bold" : ""}>
            {stock}
          </span>
        );
      },
      sorter: (a, b) => (a.currentStock || 0) - (b.currentStock || 0),
    },
    {
      title: "Min. Level", dataIndex: "minimumStockLevel", align: "center", width: 100,
      render: (v) => v || 0,
    },
    {
      title: "Purchase Rate (₹)", dataIndex: "purchaseRate", align: "right", width: 150,
      render: fmt,
    },
    {
      title: "Stock Value (₹)", align: "right", width: 140,
      render: (_, r) => fmt((r.currentStock || 0) * (r.purchaseRate || 0)),
    },
    {
      title: "Status", dataIndex: "status", width: 90,
      render: (v) => <span className={`badge bg-${v === "Active" ? "success" : "secondary"}`}>{v}</span>,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Current Stock" subtitle="Real-time stock levels for all products" />
        {lowStockCount > 0 && (
          <div className="alert alert-warning py-2 d-flex align-items-center gap-2 mb-3">
            <i className="fas fa-exclamation-triangle" />
            <span><strong>{lowStockCount}</strong> product(s) are at or below minimum stock level.</span>
          </div>
        )}
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="search-input" style={{ minWidth: 200 }}>
                <input type="text" placeholder="Search product..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="d-flex gap-1">
                {categories.map((cat) => (
                  <button key={cat}
                    className={`btn btn-sm ${categoryFilter === cat ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setCategoryFilter(cat)}>
                    {cat}
                  </button>
                ))}
              </div>
              <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => dispatch(fetchCurrentStock())}>
                Refresh
              </button>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentStock;
